import {Store} from "@/plugins/Store";
import {ArticlePart, ArticlePartContent} from "@/model/Article";
import {ArticleMetasConfig} from "@/config/ArticleConfig";
import {FALLBACK_URL, getBaseUrl, getSuggestedUrl} from "@/utils/ArticleUrl";
import {stripTagsAndEntities} from "@/utils/string";
import {SeoScoreMetricName} from "@/model/SeoScore";
import {generateArticleSchemaJsonLd} from "@/service/seoScoreSchema";
import {Image} from "@/model/Scrapper";

export type SeoImproveAction = 'A' | 'B' | 'C';

export type SeoImproveResult = {
    applied: boolean;
    needsEditor?: boolean;
    action: SeoImproveAction;
};

export const seoImprovePlan: Record<SeoScoreMetricName, { generate: SeoImproveAction; url: SeoImproveAction }> = {
    aiVisibility: {generate: 'C', url: 'C'},
    eeatScore: {generate: 'B', url: 'B'},
    eatAuthor: {generate: 'B', url: 'B'},
    eatDate: {generate: 'B', url: 'B'},
    eatBio: {generate: 'B', url: 'B'},
    schemaMarkup: {generate: 'A', url: 'A'},
    faqPresent: {generate: 'C', url: 'C'},
    keywordVariants: {generate: 'C', url: 'C'},
    relatedKeywords: {generate: 'C', url: 'C'},
    keywordInSlug: {generate: 'A', url: 'B'},
    keywordInH1: {generate: 'C', url: 'C'},
    keywordInIntroduction: {generate: 'C', url: 'C'},
    keywordInH2: {generate: 'C', url: 'C'},
    keywordInH3: {generate: 'C', url: 'C'},
    keywordDensity: {generate: 'C', url: 'C'},
    avgWordCountOfTopTen: {generate: 'C', url: 'C'},
    longestWordCountOfTopTen: {generate: 'C', url: 'C'},
    subHeadingDistribution: {generate: 'A', url: 'A'},
    paragraphLength: {generate: 'C', url: 'C'},
    sentenceLength: {generate: 'C', url: 'C'},
    inspirationFromTopTen: {generate: 'B', url: 'B'},
    outboundLinks: {generate: 'B', url: 'B'},
    inboundLinks: {generate: 'B', url: 'B'},
    imgAltIncludesKeyword: {generate: 'A', url: 'A'},
    titleIncludesKeyword: {generate: 'C', url: 'C'},
    titleLength: {generate: 'C', url: 'C'},
    descriptionIncludesKeyword: {generate: 'C', url: 'C'},
    descriptionLength: {generate: 'C', url: 'C'},
    serpIsComplete: {generate: 'A', url: 'A'},
};

function article() {
    return Store.getters.article;
}

function improveMode(): 'generate' | 'url' {
    return article().isAnalyze ? 'url' : 'generate';
}

export function improveActionFor(metricName: SeoScoreMetricName): SeoImproveAction {
    return seoImprovePlan[metricName][improveMode()];
}

function keyword(): string {
    return (article().selectedKeyword || article().keyword || '').trim();
}

function editorResult(action: SeoImproveAction): SeoImproveResult {
    return {applied: false, needsEditor: true, action};
}

function applied(action: SeoImproveAction = 'A'): SeoImproveResult {
    return {applied: true, action};
}

function improveSchema(): SeoImproveResult {
    Store.commit('setArticleSchemaJsonLd', generateArticleSchemaJsonLd(article()));
    return applied();
}

function improveSlug(): SeoImproveResult {
    if (improveMode() === 'url') return editorResult('B');
    const search = keyword();
    const current = article().url;
    const base = current ? getBaseUrl(current) : FALLBACK_URL;
    Store.commit('setArticleUrl', getSuggestedUrl(base, search));
    return applied();
}

function improveImageAlt(): SeoImproveResult {
    const search = keyword();
    const images: Image[] = article().images.map((image: Image) => ({...image}));
    if (!images.length) return editorResult('B');

    let changed = false;
    images.forEach((image, index) => {
        if (!stripTagsAndEntities(image.alt || '')) {
            images[index].alt = search;
            changed = true;
        }
    });
    if (!changed) return editorResult('C');

    Store.commit('setArticleImages', {images});
    return applied();
}

function improveSerp(): SeoImproveResult {
    let changed = false;
    if (!stripTagsAndEntities(article().metaTitle)) {
        Store.commit('setArticleMeta', {
            metaPart: 'metaTitle',
            value: stripTagsAndEntities(article().h1[0]?.h || keyword()),
        });
        changed = true;
    }
    if (!stripTagsAndEntities(article().metaDescription)) {
        const source = stripTagsAndEntities(article().h1[0]?.p || article().h1[0]?.h || keyword());
        Store.commit('setArticleMeta', {
            metaPart: 'metaDescription',
            value: source.slice(0, ArticleMetasConfig.metaDescription.suggestedLength),
        });
        changed = true;
    }
    return changed ? applied() : editorResult('C');
}

function improveSubheadings(): SeoImproveResult {
    let changed = false;
    article().getAllParts().forEach((part: ArticlePart) => {
        const contentPart = ('h' + part.number) as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
        part.value.forEach((block: ArticlePartContent, index: number) => {
            if (stripTagsAndEntities(block.p) && !stripTagsAndEntities(block.h)) {
                const heading = stripTagsAndEntities(block.p).split(/[.!?]/)[0].slice(0, 70).trim();
                if (heading) {
                    Store.commit('setArticleContent', {
                        contentPart,
                        index,
                        contentKey: 'h',
                        value: heading,
                    });
                    changed = true;
                }
            }
        });
    });
    return changed ? applied() : editorResult('A');
}

const mechanicalImprovers: Partial<Record<SeoScoreMetricName, () => SeoImproveResult>> = {
    schemaMarkup: improveSchema,
    keywordInSlug: improveSlug,
    imgAltIncludesKeyword: improveImageAlt,
    serpIsComplete: improveSerp,
    subHeadingDistribution: improveSubheadings,
};

export function improveSeoMetric(metricName: SeoScoreMetricName): SeoImproveResult {
    const action = improveActionFor(metricName);
    if (action !== 'A') {
        return editorResult(action);
    }
    const improver = mechanicalImprovers[metricName];
    if (!improver) {
        return editorResult('B');
    }
    return improver();
}

export function canAutoImproveMetric(metricName: SeoScoreMetricName): boolean {
    return improveActionFor(metricName) === 'A';
}
